import { LitElement, html, property } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { capitalize, cleanString } from '@polleverywhere/core/lib/util'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './text-field.scss'
import '@polleverywhere/pe-graphic'

/**
 * Renders a text input field with validation checks based on native validity
 * states from the input field. This is essentially a wrapper around an
 * `<input>` or `<textarea>` element that exposes a consistent way for error
 * messages to be rendered back to the user.
 *
 * ```
 * // Single-line email text field
 * <pe-text-field type='email' required></pe-text-field>
 *
 * // Multi-line required text field
 * <pe-text-field type='textarea' required></pe-text-field>
 *
 * ```
 *
 * Custom error messages are supported and can simply be set as an attribute on
 * the element itself.
 * ```
 * <pe-text-field error='Something went wrong.'></pe-text-field>
 * ```
 *
 * Whenever the input field is invalid, an error message is included as an
 * attribute at the top-level. Consumers can check if the input field is valid
 * either by checking if the `error` attribute exists or calling the `valid()`
 * method.
 *
 * Consumers control how and when the text field undergoes validation.
 * Typically, you'll want to do this by simply listening for an `<input>` event
 * of your choice, i.e. `change`, `input`, `blur`, etc. and calling `validate()`
 * from within your event handler.
 *
 * ```
 * // Validate the text field whenever the input changes.
 * const tf = this.querySelector('pe-text-field')
 * tf.addEventListener('input', (e) => tf.validate())
 * ```
 *
 * @element pe-text-field
 *
 * @prop {string} autocapitalize - Set to `off` to disable browser auto capitalization
 * @prop {string} autocomplete - Set to hint browser what to autocomplete
 * @prop {string} autocorrect - Set to `off` to disable browser auto correction
 * @prop {boolean} charCounter - Requires `maxLength` to be set and renders a
 *  character counter that live updates as users type into the field. This is
 *  only useful for fields that apply a max char limit, e.g. username and
 *  password fields.
 * @prop {number} cols - For textarea types, number of columns.
 * @prop {boolean} disabled - Disables the text field from input.
 * @prop {string} error - Contains the error validation message whenever the
 *  input field is in an invalid state. Consumers can set this attribute to
 *  manually trigger their own validation error.
 * @prop {string} helper - Additional help text to display below the text field.
 *  When in an error state, this help text is replaced with the error validation
 *  message. When error state is cleared, help text resumes showing.
 * @prop {string} icon - Leading icon glyph for single-line text fields.
 * @prop {string} iconTrailing - Trailing icon glyph for single-line text
 *  fields. Colors change when in an error state.
 * @prop {number} max - Max range limit.
 * @prop {number} maxLength - Max char limit.
 * @prop {number} min - Min range limit.
 * @prop {number} minLength - Min char limit.
 * @prop {string} name - Give the `<input>` element a name. This name will also
 *  show up in validation messages as the subject.
 * @prop {string} pattern - Regular expression pattern to match.
 * @prop {string} placeholder - Help text to display in the input field.
 * @prop {boolean} readonly - Make the input field readonly.
 * @prop {boolean} required - Make the input field required.
 * @prop {number} rows - For textarea types, number of rows.
 * @prop {boolean} sanitize - Sanitizes value for xss
 * @prop {string} spellcheck - Set to `true` or `false` to explicitly toggle browser spellcheck
 * @prop {string} type - Text-based input types such as email, url, password,
 *  etc. To render a multi-line text areas, use type value `textarea` instead.
*   Keep in mind, not all types are supported and can cause issues.
 * @prop {string} value - Default value to initialize input field to. Consumers
 *  should use the value property to update the input field afterwards.
 * @prop {boolean} useDefaultPlaceholder - Enable native placeholder behavior
 *  instead of our custom animating one.
 *
 * @slot before - Element slot is inserted before (or to the left of) the input
 *  field. Consumers can use this to prefix the input with additional content.
 * @slot after - Element slot is inserted after (or to the right of) the input
 *  field. Consumers can use this to suffix the input with additional content.
 */
export default class TextField extends LitElement {
  static get tagName () { return 'pe-text-field' }

  @property({ type: String })
  autocapitalize

  @property({ type: String })
  autocomplete

  @property({ type: String })
  autocorrect

  @property({ type: Boolean, attribute: 'char-counter' })
  charCounter

  @property({ type: Number })
  cols

  @property({ type: Boolean })
  disabled

  @property({ type: String })
  error

  @property({ type: String })
  helper

  @property({ type: String })
  icon

  @property({ type: String, attribute: 'icon-trailing' })
  iconTrailing

  @property({ type: Number })
  max

  @property({ type: Number })
  maxLength

  @property({ type: Number })
  min

  @property({ type: Number })
  minLength

  @property({ type: String })
  name

  @property({ type: String })
  pattern

  @property({ type: String })
  placeholder

  @property({ type: Boolean })
  readonly

  @property({ type: Boolean })
  required

  @property({ type: Number })
  rows

  @property({ type: Boolean })
  sanitize

  @property({ type: String })
  spellcheck

  @property({ type: String })
  type

  @property({ type: String })
  value

  @property({
    type: Boolean,
    attribute: 'use-default-placeholder',
    reflect: true
  })
  useDefaultPlaceholder

  /**
   * (READONLY) Evaluates whether the input field is in an "active" state,
   * either containing some input or having document focus.
   * @return {boolean}
   */
  get active () {
    const field = this.field
    return field
      ? !!field.value.length || this.focused
      : !!this.getAttribute('value')
  }

  /**
   * (READONLY) Returns either the input or textarea element field.
   * @return {HTMLElement}
   */
  get field () {
    return this.querySelector('input') || this.querySelector('textarea')
  }

  /**
   * (READONLY) Returns whether the input field has document and active focus.
   * @return {boolean}
   */
  get focused () {
    return document.hasFocus() && document.activeElement === this.field
  }

  /**
   * (READONLY) Returns whether the input is currently in a valid state or not.
   * @return {boolean}
   */
  get valid () {
    const field = this.field
    const disabledValue = field.disabled

    // Temporarily disable input before checking validity. Otherwise, disabled
    // inputs always return valid and ignores all constraint validations.
    field.disabled = false
    const valid = field.validity.valid && !this.error
    field.disabled = disabledValue

    return valid
  }

  get value () {
    const field = this.field
    const val = field
      ? field.value
      : this.getAttribute('value') || ''

    return this.sanitize ? cleanString(val) : val
  }

  set value (val) {
    const field = this.field
    if (!field) return

    field.value = val
    this.requestUpdate()
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this, 'before')
    defineElementSlot(this, 'after')
  }

  createRenderRoot () { return this }

  render () {
    const mods = {
      counter: this.charCounter,
      disabled: this.disabled,
      error: !!this.error,
      focused: this.focused,
      helper: !!this.helper || !!this.error,
      multiline: this.type === 'textarea'
    }

    return html`
    <div class=${bem('wrapper', mods)}>
      <label class=${bem('input-row', { active: this.active })}
          @mousedown=${handleInputFocus}>
        ${this.slots('before')}
        ${renderIcon.call(this)}
        ${this.type === 'textarea'
          ? renderTextArea.call(this)
          : renderInput.call(this)}
        ${renderIcon.call(this, true)}
        ${renderPlaceholder.call(this)}
        <span class=${bem('line')}></span>
        ${this.slots('after')}
      </label>
      <div class=${bem('helper-row')}>
        <span class=${bem('helper')}
            role=${ifDefined(this.error ? 'alert' : undefined)}>
          ${this.error || this.helper}
        </span>
        ${renderCharCounter.call(this)}
      </div>
    </div>
    `
  }

  /**
   * Sets focus to the input field.
   */
  focus () {
    this.updateComplete.then(() => this.field.focus())
  }

  /**
   * Selects all text in the input field.
   */
  select () {
    this.updateComplete.then(() => this.field.select())
  }

  /**
   * Validate the input field and toggle the error state.
   * @return {boolean} True if input field is valid. False otherwise.
   */
  validate () {
    const input = this.field
    const validity = input.validity
    const subject = capitalize(input.name.replace(/(.+\[([^\]]+)\])/, '$2').replace(/[_-]/g, ' ') || 'text field')

    // Override default validation messages with our own. All validity states
    // come with their own default messaging, which we can always revert back to
    // by removing the custom validity message for that property.
    if (validity.valueMissing) {
      input.setCustomValidity(`${subject} must have a value.`)
    } else if (validity.tooLong) {
      input.setCustomValidity(
        `${subject} must have ${input.maxLength} characters or less.`)
    } else if (validity.tooShort) {
      input.setCustomValidity(
        `${subject} must have at least ${input.minLength} characters.`)
    } else if (validity.typeMismatch) {
      input.setCustomValidity(`${subject} must be a valid ${humanizedType(input.type)}.`)
    } else if (validity.patternMismatch) {
      input.setCustomValidity(`${subject} must match the requested format.`)
    } else {
      input.setCustomValidity('')
    }

    // Toggles error on/off depending on the input's validation message, which
    // will auto-populate whenever it is invalid.
    if (input.validationMessage) {
      this.error = input.validationMessage
    } else {
      this.error = ''
    }

    this.requestUpdate()
    return this.valid
  }
}

function renderPlaceholder () {
  if (this.useDefaultPlaceholder || !this.placeholder) return
  return html`
  <span class=${bem('placeholder')}>
    ${this.placeholder}
  </span>
  `
}

function renderTextArea () {
  return html`<textarea class=${bem('textarea')}
    aria-label=${ifDefined(this.placeholder || undefined)}
    autocapitalize=${ifDefined(this.autocapitalize)}
    autocomplete=${ifDefined(this.autocomplete)}
    autocorrect=${ifDefined(this.autocorrect)}
    cols=${ifDefined(normalizeNumber(this.cols))}
    ?disabled=${this.disabled}
    maxLength=${ifDefined(normalizeNumber(this.maxLength))}
    minLength=${ifDefined(normalizeNumber(this.minLength))}
    name=${ifDefined(this.name)}
    placeholder=${ifDefined(this.placeholder || undefined)}
    ?readonly=${this.readonly}
    ?required=${this.required}
    rows=${ifDefined(normalizeNumber(this.rows))}
    spellcheck=${ifDefined(this.spellcheck)}
    @blur=${elementRequestUpdate}
    @focus=${elementRequestUpdate}
    @input=${elementRequestUpdate}
    @mousedown=${eventStopPropagation}
  >${this.sanitize ? cleanString(this.value) : this.value}</textarea>`
}

function renderInput () {
  return html`<input class=${bem('input')}
    aria-label=${ifDefined(this.placeholder || undefined)}
    autocapitalize=${ifDefined(this.autocapitalize)}
    autocomplete=${ifDefined(this.autocomplete)}
    autocorrect=${ifDefined(this.autocorrect)}
    ?disabled=${this.disabled}
    max=${ifDefined(normalizeNumber(this.max))}
    maxLength=${ifDefined(normalizeNumber(this.maxLength))}
    min=${ifDefined(normalizeNumber(this.min))}
    minLength=${ifDefined(normalizeNumber(this.minLength))}
    name=${ifDefined(this.name)}
    pattern=${ifDefined(this.pattern || undefined)}
    placeholder=${ifDefined(this.placeholder || undefined)}
    ?readonly=${this.readonly}
    ?required=${this.required}
    spellcheck=${ifDefined(this.spellcheck)}
    type=${ifDefined(this.type)}
    .value=${this.sanitize ? cleanString(this.value) : this.value}
    @blur=${elementRequestUpdate}
    @focus=${elementRequestUpdate}
    @input=${elementRequestUpdate}
    @mousedown=${eventStopPropagation}
  />`
}

function eventStopPropagation (e) {
  e.stopPropagation()
}

function elementRequestUpdate () {
  this.requestUpdate()
}

function renderIcon (trailing = false) {
  const name = trailing ? this.iconTrailing : this.icon
  if (!name || this.type === 'textarea') return

  return html`<pe-graphic
    class=${bem('icon', { trailing })}
    name=${name}
    type='icon'
  ></pe-graphic>`
}

function renderCharCounter () {
  if (!(this.maxLength > 0)) return

  const field = this.field
  const value = field ? field.value : this.getAttribute('value') || ''
  const content = this.charCounter ? `${value.length} / ${this.maxLength}` : ''
  return html`<span class=${bem('counter')}>${content}</span>`
}

function handleInputFocus (e) {
  e.preventDefault()

  // Only select field text for single-line inputs that do not have focus.
  const field = this.field
  document.activeElement !== field &&
    this.type !== 'textarea' &&
    field.select()

  field.focus()
}

function normalizeNumber (value) {
  return typeof value === 'number' ? value : undefined
}

function humanizedType (type) {
  switch (type) {
    case 'url':
      return type.toUpperCase()
    case 'email':
      return 'email address'
    default:
      return type
  }
}

const bem = bemBlock(TextField.tagName)
register(module, TextField.tagName, TextField)
